import { CloudAccountGetResponse } from '@ariksa/cloud-account/api';
import {
  AccessSourceAccount,
  AccessSourceItem,
  AccessSourceQuery,
  ResourceBase,
  SearchQuery,
  SourcePagination,
} from '@ariksa/inventory-core/api';
import { Optional } from 'types/utils';

import { getIcon } from 'components/Icons/Components';
import {
  SourceNodeOptions,
  SourcePaging,
} from 'containers/Visibility/SecurityGraphNext/types';

// The entity dropdown's page size, for the map's first page and every later one.
export const SOURCE_PAGE_SIZE = 50;

interface SourceGroup {
  native_type: string;
  display_name?: string | null;
  items?: Array<ResourceBase | AccessSourceItem>;
}

type SourceOption = NonNullable<SourceNodeOptions[number]['options']>[number];

// Dropdown groups, one per type. Each entry keeps its account and type,
// which the Investigate view reads from the selected entry.
export const toSourceNodeOptions = (
  groups: SourceGroup[] = [],
): SourceNodeOptions =>
  groups.map(group => ({
    label: group.display_name ?? group.native_type,
    value: {
      display_name: group.display_name ?? undefined,
      native_name: group.native_type,
    },
    options: group.items?.map(item => ({
      label: (item.name || item.resource_id) ?? undefined,
      value: {
        ...item,
        account: item.account_id,
        native_name: group.native_type,
      },
      icon: getIcon(group.native_type),
    })),
  }));

// A page from /search/access-sources, grouped by type in the page's order.
export const groupSourcePage = (
  items: AccessSourceItem[] = [],
): SourceGroup[] => {
  const groups = new Map<string, SourceGroup>();
  items.forEach(item => {
    const group = groups.get(item.native_type) ?? {
      native_type: item.native_type,
      display_name: item.display_name,
      items: [],
    };
    group.items!.push(item);
    groups.set(item.native_type, group);
  });
  return Array.from(groups.values());
};

// Adds a later page to the loaded entries, each into its type's group. A page
// can repeat an entry when the graph changes between requests, so an entry
// already listed is skipped.
export const mergeSourceNodeOptions = (
  options: SourceNodeOptions,
  page: SourceNodeOptions,
): SourceNodeOptions => {
  const listed = new Set(
    options.flatMap(group => group.options?.map(o => o.value?.uuid) ?? []),
  );
  const merged = options.map(group => ({
    ...group,
    options: [...(group.options ?? [])],
  }));
  page.forEach(group => {
    const added =
      group.options?.filter(option => !listed.has(option.value?.uuid)) ?? [];
    if (!added.length) return;
    const existing = merged.find(
      g => g.value?.native_name === group.value?.native_name,
    );
    if (existing) existing.options.push(...added);
    else merged.push({ ...group, options: added });
  });
  return merged;
};

// The paging state for a list response; null when the server listed every source.
export const toSourcePaging = (
  pagination: Optional<SourcePagination>,
  request: AccessSourceQuery,
): Optional<SourcePaging> =>
  pagination
    ? {
        request,
        page: pagination.page,
        size: pagination.size,
        total: pagination.total,
        hasMore: pagination.page * pagination.size < pagination.total,
        accounts: pagination.accounts ?? [],
      }
    : null;

// The /search/access-sources query for a search-bar map request.
export const accessSourceQueryOf = (query: SearchQuery): AccessSourceQuery => ({
  uuid: query.uuid,
  query: query.query,
  wrapper: query.wrapper,
  environment_id: query.environment_id ?? undefined,
  account_id: query.account_ids ?? undefined,
});

// The dropdown entry for a source in the response's source list.
export const findSourceOption = (
  groups: SourceGroup[] | null | undefined,
  uuid: string,
): SourceOption | undefined =>
  toSourceNodeOptions(groups ?? [])
    .flatMap(group => group.options ?? [])
    .find(option => option.value?.uuid === uuid);

// Shows each entry's cloud account under its name, as `name (number)`. An
// account that isn't onboarded, such as an external AWS account, shows none.
export const withAccountLines = (
  options: SourceNodeOptions,
  accounts: Record<string, CloudAccountGetResponse>,
): SourceNodeOptions =>
  options.map(group =>
    // react-select treats any entry with an `options` key as a group, so a
    // flat entry stays as it is
    Array.isArray(group.options)
      ? {
          ...group,
          options: group.options.map(option => {
            const account = accounts[option.value?.account ?? ''];
            return account
              ? {
                  ...option,
                  subLabel: `${account.name} (${account.cloud_account_id})`,
                }
              : option;
          }),
        }
      : group,
  );

// Whether a picked source's map request lists the same sources as the paged
// list: the same query, by text when both have it and otherwise by uuid, in
// the paged list's environment. The resource map's list has no environment.
export const isSameSourceList = (
  paged: AccessSourceQuery,
  request: AccessSourceQuery,
): boolean => {
  const sameQuery =
    paged.query && request.query
      ? paged.query === request.query
      : !!paged.uuid && paged.uuid === request.uuid;
  const sameEnvironment =
    !paged.environment_id || paged.environment_id === request.environment_id;
  return sameQuery && sameEnvironment;
};

// The number of entries in the dropdown's groups.
export const countSourceOptions = (options: SourceNodeOptions): number =>
  options.reduce((count, group) => count + (group.options?.length ?? 0), 0);

// The account picker's entry for every account that isn't onboarded, such as
// an AWS account that only assumes a role, and for sources without an account:
// each would be an entry with one principal, so they share one.
export const EXTERNAL_ACCOUNTS = 'external';

export interface SourceAccountFacet {
  key: string;
  label: string;
  count: number;
  // the account ids to filter by; null picks the sources without an account
  ids: Array<string | null>;
  // the cloud account number and provider; none for the external entry
  number?: string;
  cloudType?: string;
}

// The picker's entries from the server's account counts: onboarded accounts by
// name, then one external entry for the rest.
export const sourceAccountFacets = (
  accountCounts: AccessSourceAccount[],
  accounts: Record<string, CloudAccountGetResponse>,
): SourceAccountFacet[] => {
  const onboarded: SourceAccountFacet[] = [];
  const external: SourceAccountFacet = {
    key: EXTERNAL_ACCOUNTS,
    label: 'External accounts',
    count: 0,
    ids: [],
  };
  accountCounts.forEach(({ account_id, count }) => {
    const account = account_id ? accounts[account_id] : undefined;
    if (account && account_id) {
      onboarded.push({
        key: account_id,
        label: account.name || account.cloud_account_id || account_id,
        count,
        ids: [account_id],
        number: account.cloud_account_id ?? undefined,
        cloudType: account.cloud_type ?? undefined,
      });
    } else {
      external.ids.push(account_id ?? null);
      external.count += count;
    }
  });
  onboarded.sort((a, b) => a.label.localeCompare(b.label));
  return external.count ? [...onboarded, external] : onboarded;
};

// The picker's entries split by the resource's account: its own account, and
// every other account, external ones included, which reach it across accounts.
export const splitAccountFacets = (
  facets: SourceAccountFacet[],
  resourceAccountId: string,
): { same: SourceAccountFacet[]; cross: SourceAccountFacet[] } => ({
  same: facets.filter(facet => facet.key === resourceAccountId),
  cross: facets.filter(facet => facet.key !== resourceAccountId),
});

// The picker entry a list's request filters by; '' for every account.
export const pickedAccountKey = (
  request: Optional<AccessSourceQuery>,
  facets: SourceAccountFacet[],
): string => {
  const ids = (request?.source_account_ids ?? []) as Array<string | null>;
  if (!ids.length) return '';
  const onboarded = facets.find(
    facet =>
      facet.key !== EXTERNAL_ACCOUNTS &&
      ids.length === 1 &&
      facet.key === ids[0],
  );
  return onboarded?.key ?? EXTERNAL_ACCOUNTS;
};

export interface SourceMenuText {
  header?: string;
  footer?: string;
}

// The menu's count header and footer: how much of the list is loaded and what
// scrolling or typing does next. An unpaged list needs neither.
export const sourceMenuText = ({
  paging,
  loaded,
  accountLabel,
  isLoading,
  search,
}: {
  paging: Optional<SourcePaging>;
  loaded: number;
  accountLabel?: string;
  isLoading: boolean;
  search?: Optional<{ count: number; total: number; hasMore: boolean }>;
}): SourceMenuText => {
  if (!paging) return {};
  // the server filters by the picked account, so the counts are that account's
  const inAccount = accountLabel ? ` in ${accountLabel}` : '';
  if (search) {
    return {
      header: `${search.count} of ${search.total} matches${inAccount}`,
      footer: search.hasMore ? 'Type more to narrow' : undefined,
    };
  }
  const remaining = Math.max(paging.total - loaded, 0);
  return {
    header: accountLabel
      ? `${loaded} of ${paging.total}${inAccount}`
      : `${loaded} of ${paging.total} entities`,
    footer: isLoading
      ? 'Loading more…'
      : paging.hasMore
      ? `Scroll for ${remaining} more`
      : `All ${paging.total} loaded`,
  };
};
